import React, { useEffect, useState } from "react";
import {
  AccountBalanceWalletOutlined,
  ArrowForwardOutlined,
  HowToRegOutlined,
  Inventory2Outlined,
  LightbulbOutlined,
  PeopleAltOutlined,
  ReceiptLongOutlined,
  RefreshOutlined,
  SavingsOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Header from "components/Header";
import { fetchDashboardSummary } from "state/dashboardAction";

const EMPTY_SUMMARY = {
  period: { label: "Periode berjalan" },
  overview: {
    employees: { total: 0, active: 0, inactive: 0 },
    attendance: {
      employeesPresent: 0,
      employeesAbsent: 0,
      sessions: 0,
      lateMinutes: 0,
    },
    setoran: { total: 0, approved: 0, pending: 0, nominal: 0 },
    savings: { accounts: 0, balance: 0 },
    operations: {
      activeWarnings: 0,
      aspirations: 0,
      activeStockOpname: 0,
      totalStockOpname: 0,
    },
  },
  trends: { attendance: [], setoran: [] },
  recent: { warnings: [], aspirations: [] },
};

const formatNumber = (value) =>
  new Intl.NumberFormat("id-ID", { maximumFractionDigits: 0 }).format(
    Number(value || 0),
  );

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  if (amount >= 1000000000) return `Rp ${(amount / 1000000000).toFixed(1)} M`;
  if (amount >= 1000000) return `Rp ${(amount / 1000000).toFixed(1)} jt`;
  if (amount >= 1000) return `Rp ${(amount / 1000).toFixed(0)} rb`;
  return `Rp ${formatNumber(amount)}`;
};

const periodLabel = (period) => {
  if (!period) return "-";
  const [year, month] = period.split("-").map(Number);
  return new Intl.DateTimeFormat("id-ID", { month: "short" }).format(
    new Date(year, month - 1, 1),
  );
};

const getGreeting = (date = new Date()) => {
  const hour = date.getHours();
  if (hour >= 4 && hour < 11) return "Good Morning";
  if (hour >= 11 && hour < 15) return "Good Afternoon";
  if (hour >= 15 && hour < 18) return "Good Evening";
  return "Good Night";
};

const MetricCard = ({ title, value, detail, icon, color, onClick }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        minHeight: 158,
        minWidth: 0,
        p: 2.25,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
        cursor: onClick ? "pointer" : "default",
        transition: "transform .2s ease, box-shadow .2s ease",
        "&:hover": onClick
          ? { transform: "translateY(-3px)", boxShadow: 3 }
          : undefined,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Typography variant="h6" color="text.secondary" fontWeight={600}>
          {title}
        </Typography>
        <Avatar sx={{ bgcolor: alpha(color, 0.1), color, width: 42, height: 42 }}>
          {icon}
        </Avatar>
      </Stack>
      <Typography
        variant="h3"
        fontWeight={800}
        color="text.primary"
        sx={{ mt: 2, fontSize: { xs: "1.8rem", sm: "2rem" }, overflowWrap: "anywhere" }}
      >
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {detail}
      </Typography>
    </Paper>
  );
};

const SectionCard = ({ title, subtitle, children, action }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        p: { xs: 2, sm: 2.5 },
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        gap={1.5}
      >
        <Box>
          <Typography variant="h5" fontWeight={800}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action && <Box sx={{ alignSelf: { xs: "flex-start", sm: "auto" } }}>{action}</Box>}
      </Stack>
      {children}
    </Paper>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const summary = useSelector((state) => state.dashboard.summary) || EMPTY_SUMMARY;
  const user = useSelector((state) => state.user.user_data);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const { overview, trends, recent, period } = summary;
  const { employees, attendance, setoran, savings, operations } = overview;

  useEffect(() => {
    dispatch(fetchDashboardSummary());
  }, [dispatch]);

  const maxAttendance = Math.max(
    ...trends.attendance.map((item) => Number(item.total || 0)),
    1,
  );
  const maxSetoran = Math.max(
    ...trends.setoran.map((item) => Number(item.nominal || 0)),
    1,
  );
  const attendanceRate = employees.active
    ? Math.min((attendance.employeesPresent / employees.active) * 100, 100)
    : 0;
  const setoranApprovalRate = setoran.total
    ? Math.min((setoran.approved / setoran.total) * 100, 100)
    : 0;

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 70px)",
        width: "100%",
        overflowX: "hidden",
        background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.alt} 100%)`,
        p: { xs: 2, sm: 3, md: "2rem 2.5rem" },
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "flex-end" }}
        gap={2}
      >
         <Box>
           <Typography variant="overline" sx={{ color: theme.palette.secondary.main, fontWeight: 800, letterSpacing: 1.4 }}>
              {getGreeting(now)}, {user?.nama_karyawan?.split(" ")[0] || "TEAM"}
           </Typography>
           <Header title="Dashboard" subtitle="Ringkasan operasional Prima Centre" />
         </Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
            <Typography variant="caption" color="text.secondary">
              Periode aktif
            </Typography>
            <Typography fontWeight={800}>{period.label}</Typography>
          </Box>
          <IconButton
            aria-label="Refresh dashboard"
            title="Refresh dashboard"
            onClick={() => dispatch(fetchDashboardSummary(true))}
            sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
          >
            <RefreshOutlined />
          </IconButton>
        </Stack>
      </Stack>

      <Box
        sx={{
          mt: 3,
          minWidth: 0,
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: 2,
          color: theme.palette.common.white,
          backgroundColor: theme.palette.primary[800],
          backgroundImage: [
            `radial-gradient(115% 135% at 100% 0%, ${alpha(theme.palette.primary[600], 0.5)} 0%, transparent 52%)`,
            `repeating-linear-gradient(135deg, ${alpha("#ffffff", 0.035)} 0px, ${alpha("#ffffff", 0.035)} 1px, transparent 1px, transparent 9px)`,
          ].join(", "),
          border: `1px solid ${alpha(theme.palette.primary[900], 0.7)}`,
          boxShadow: `inset 0 1px 0 ${alpha("#ffffff", 0.05)}`,
          position: "relative",
          overflow: "hidden",
          "&:before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            backgroundColor: theme.palette.secondary.main,
          },
        }}
      >
        <Typography variant="overline" sx={{ opacity: 0.72, letterSpacing: 1.5 }}>
          COMMAND CENTER
        </Typography>
        <Typography variant={isMobile ? "h4" : "h3"} fontWeight={800} sx={{ mt: 0.5, maxWidth: 650 }}>
          Pantau bisnis dalam satu tampilan.
        </Typography>
        <Typography sx={{ mt: 1, opacity: 0.8, maxWidth: 650 }}>
          Data ringkasan diambil dari tabel operasional dan diperbarui saat dashboard dibuka.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          minWidth: 0,
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
          gap: 2,
          mt: 2,
        }}
      >
        <MetricCard
          title="Karyawan Aktif"
          value={formatNumber(employees.active)}
          detail={`${formatNumber(employees.total)} total karyawan`}
          color={theme.palette.primary[400]}
          icon={<PeopleAltOutlined />}
          onClick={() => navigate("/karyawanbaru")}
        />
        <MetricCard
          title="Hadir Hari Ini"
          value={formatNumber(attendance.employeesPresent)}
          detail={`${formatNumber(attendance.employeesAbsent)} belum tercatat`}
          color={theme.palette.secondary[300]}
          icon={<HowToRegOutlined />}
          onClick={() => navigate("/absensihariini")}
        />
        <MetricCard
          title="Setoran Bulan Ini"
          value={formatCurrency(setoran.approvedNominal || setoran.nominal)}
          detail={`${formatNumber(setoran.approved)} disetujui · ${formatNumber(setoran.pending)} menunggu`}
          color={theme.palette.secondary[500]}
          icon={<AccountBalanceWalletOutlined />}
          onClick={() => navigate("/setoran")}
        />
        <MetricCard
          title="Saldo Tabungan"
          value={formatCurrency(savings.balance)}
          detail={`${formatNumber(savings.accounts)} akun karyawan`}
          color={theme.palette.primary[300]}
          icon={<SavingsOutlined />}
          onClick={() => navigate("/tabungan")}
        />
        <MetricCard
          title="Peringatan Aktif"
          value={formatNumber(operations.activeWarnings)}
          detail="Perlu perhatian manajemen"
          color={theme.palette.secondary[600]}
          icon={<WarningAmberOutlined />}
          onClick={() => navigate("/peringatan")}
        />
        <MetricCard
          title="Stock Opname Aktif"
          value={formatNumber(operations.activeStockOpname)}
          detail={`${formatNumber(operations.totalStockOpname)} agenda tercatat`}
          color={theme.palette.primary[500]}
          icon={<Inventory2Outlined />}
          onClick={() => navigate("/stockopname")}
        />
        <MetricCard
          title="Aspirasi Masuk"
          value={formatNumber(operations.aspirations)}
          detail="Total aspirasi karyawan"
          color={theme.palette.secondary[400]}
          icon={<LightbulbOutlined />}
          onClick={() => navigate("/aspirasi")}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          minWidth: 0,
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.7fr) minmax(300px, 1fr)" },
          gap: 2,
          mt: 2,
        }}
      >
        <SectionCard title="Tren 6 Bulan" subtitle="Perbandingan aktivitas absensi dan nominal setoran">
          <Box sx={{ display: "grid", gap: 2.5, mt: 3 }}>
            <Box>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight={700}>Kehadiran</Typography>
                <Typography variant="body2" color="text.secondary">karyawan hadir</Typography>
              </Stack>
              <Stack direction="row" alignItems="flex-end" gap={{ xs: 1, sm: 1.5 }} sx={{ height: 150 }}>
                {trends.attendance.length ? trends.attendance.map((item) => (
                  <Box key={item.period} sx={{ flex: 1, height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", gap: 0.7 }}>
                    <Typography variant="caption" fontWeight={700}>{formatNumber(item.total)}</Typography>
                    <Box sx={{ width: "100%", maxWidth: 42, height: `${Math.max((item.total / maxAttendance) * 100, 5)}%`, minHeight: 8, borderRadius: "8px 8px 3px 3px", background: `linear-gradient(180deg, ${theme.palette.secondary[300]}, ${theme.palette.secondary[600]})` }} />
                    <Typography variant="caption" color="text.secondary">{periodLabel(item.period)}</Typography>
                  </Box>
                )) : <Typography color="text.secondary">Belum ada data tren absensi.</Typography>}
              </Stack>
            </Box>
            <Divider />
            <Box>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight={700}>Nominal Setoran</Typography>
                <Typography variant="body2" color="text.secondary">total per bulan</Typography>
              </Stack>
              <Stack direction="row" alignItems="flex-end" gap={{ xs: 1, sm: 1.5 }} sx={{ height: 150 }}>
                {trends.setoran.length ? trends.setoran.map((item) => (
                  <Box key={item.period} sx={{ flex: 1, height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", gap: 0.7 }}>
                    <Typography variant="caption" fontWeight={700}>{formatCurrency(item.nominal)}</Typography>
                    <Box sx={{ width: "100%", maxWidth: 42, height: `${Math.max((item.nominal / maxSetoran) * 100, 5)}%`, minHeight: 8, borderRadius: "8px 8px 3px 3px", background: `linear-gradient(180deg, ${theme.palette.primary[300]}, ${theme.palette.primary[600]})` }} />
                    <Typography variant="caption" color="text.secondary">{periodLabel(item.period)}</Typography>
                  </Box>
                )) : <Typography color="text.secondary">Belum ada data tren setoran.</Typography>}
              </Stack>
            </Box>
          </Box>
        </SectionCard>

        <SectionCard title="Operational Pulse" subtitle="Indikator yang perlu dipantau">
          <Stack gap={2.5} sx={{ mt: 3 }}>
            <Box>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.7 }}>
                <Typography variant="body2" fontWeight={700}>Kehadiran hari ini</Typography>
                <Typography variant="body2" fontWeight={800}>{Math.round(attendanceRate)}%</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={attendanceRate} sx={{ height: 9, borderRadius: 5, bgcolor: theme.palette.background.default, "& .MuiLinearProgress-bar": { bgcolor: theme.palette.secondary[300], borderRadius: 5 } }} />
              <Typography variant="caption" color="text.secondary">{formatNumber(attendance.employeesPresent)} dari {formatNumber(employees.active)} karyawan aktif</Typography>
            </Box>
            <Box>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.7 }}>
                <Typography variant="body2" fontWeight={700}>Persetujuan setoran</Typography>
                <Typography variant="body2" fontWeight={800}>{Math.round(setoranApprovalRate)}%</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={setoranApprovalRate} sx={{ height: 9, borderRadius: 5, bgcolor: theme.palette.background.default, "& .MuiLinearProgress-bar": { bgcolor: theme.palette.secondary[500], borderRadius: 5 } }} />
              <Typography variant="caption" color="text.secondary">{formatNumber(setoran.pending)} setoran menunggu persetujuan</Typography>
            </Box>
            <Divider />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" gap={1.2} alignItems="center"><ReceiptLongOutlined color="action" /><Typography variant="body2">Sesi absensi hari ini</Typography></Stack>
              <Typography fontWeight={800}>{formatNumber(attendance.sessions)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" gap={1.2} alignItems="center"><WarningAmberOutlined color="error" /><Typography variant="body2">Menit keterlambatan</Typography></Stack>
              <Typography fontWeight={800}>{formatNumber(attendance.lateMinutes)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" gap={1.2} alignItems="center"><Inventory2Outlined color="action" /><Typography variant="body2">Stock opname aktif</Typography></Stack>
              <Typography fontWeight={800}>{formatNumber(operations.activeStockOpname)}</Typography>
            </Stack>
          </Stack>
        </SectionCard>
      </Box>

      <Box sx={{ display: "grid", minWidth: 0, gridTemplateColumns: { xs: "1fr", lg: "repeat(3, minmax(0, 1fr))" }, gap: 2, mt: 2 }}>
        <SectionCard
          title="Peringatan Terbaru"
          subtitle="Lima peringatan terakhir"
          action={<Button color="secondary" size="small" endIcon={<ArrowForwardOutlined />} onClick={() => navigate("/peringatan")}>Lihat semua</Button>}
        >
          <Stack divider={<Divider flexItem />} sx={{ mt: 2 }}>
            {recent.warnings.length ? recent.warnings.map((item) => (
              <Box key={item.id} sx={{ py: 1.25 }}>
                <Typography fontWeight={700} noWrap>{item.judul_peringatan || "Peringatan tanpa judul"}</Typography>
                <Typography variant="caption" color="text.secondary">{item.nama_karyawan || item.id_karyawan || "Karyawan -"} · {item.tanggal_berlaku ? new Date(item.tanggal_berlaku).toLocaleDateString("id-ID") : "-"}</Typography>
              </Box>
            )) : <Typography color="text.secondary" sx={{ py: 2 }}>Belum ada peringatan terbaru.</Typography>}
          </Stack>
        </SectionCard>
        <SectionCard
          title="Aspirasi Terbaru"
          subtitle="Masukan terbaru dari karyawan"
          action={<Button color="secondary" size="small" endIcon={<ArrowForwardOutlined />} onClick={() => navigate("/aspirasi")}>Lihat semua</Button>}
        >
          <Stack divider={<Divider flexItem />} sx={{ mt: 2 }}>
            {recent.aspirations.length ? recent.aspirations.map((item) => (
              <Box key={item.id} sx={{ py: 1.25 }}>
                <Typography fontWeight={700} noWrap>{item.tujuan || "Aspirasi baru"}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }} noWrap>{item.masalah || "Tidak ada deskripsi"}</Typography>
                <Typography variant="caption" color="text.secondary">{item.nama_karyawan || item.id_karyawan || "Karyawan -"}</Typography>
              </Box>
            )) : <Typography color="text.secondary" sx={{ py: 2 }}>Belum ada aspirasi terbaru.</Typography>}
          </Stack>
        </SectionCard>
      </Box>
    </Box>
  );
};

export default Dashboard;
